import { useState } from "react"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"

import type { DocumentWorkflowStageDto } from "../../documentWorkflowApi"

import {
    createStageDescription,
    createStageTitle,
    getStageUiType,
    mapStageStatusToUi,
} from "../../documentWorkflowHelpers"
import type {
    UiStageType,
    WorkflowStageDialogState,
} from "../../documentWorkflowTypes"
import { AddEditWorkflowStage } from "../../dialogs/AddEditWorkflowStage"
import { DeleteWorkflowStage } from "../../dialogs/DeleteWorkflowStage"
import { getStageTypeChipSx, stageTypeChipIcons, stageTypeLabels } 
from "../../DocumentWorkflowConfig"

export type WorkflowDefinitionViewProps = {
    documentId?: number | string | null
    sections: DocumentWorkflowStageDto[]
    currentStageId: number | null
    showSetCurrentStageAction: boolean
    onStageSaved: () => Promise<void> | void
    onRemoveStage: (stageId: number) => Promise<boolean>
    onMoveStage: (stageId: number, direction: "up" | "down") => void
    onSetCurrentStage: (stageId: number) => void
}

export const WorkflowDefinitionView = ({
    documentId,
    sections,
    currentStageId,
    showSetCurrentStageAction,
    onStageSaved,
    onRemoveStage,
    onMoveStage,
    onSetCurrentStage,
}: WorkflowDefinitionViewProps) => {
    const [stageDialogState, setStageDialogState] =
        useState<WorkflowStageDialogState | null>(null)
    const [stageDialogTitle, setStageDialogTitle] = useState("")
    const [stageDialogDescription, setStageDialogDescription] = useState("")
    const [stageToDelete, setStageToDelete] =
        useState<DocumentWorkflowStageDto | null>(null)

    const handleOpenAddStageDialog = (
        stageType: Exclude<UiStageType, "EDIT">
    ) => {
        const existingCount = sections.filter(
            section => getStageUiType(section) === stageType
        ).length

        setStageDialogState({
            mode: "add",
            stageType,
        })
        setStageDialogTitle(
            createStageTitle(stageType, existingCount)
        )
        setStageDialogDescription(
            createStageDescription(stageType)
        )
    }

    const handleOpenEditStageDialog = (
        section: DocumentWorkflowStageDto
    ) => {
        setStageDialogState({
            mode: "edit",
            stageType: getStageUiType(section),
            stageId: section.id,
            stageApiType: section.type,
        })
        setStageDialogTitle(section.name ?? "")
        setStageDialogDescription(section.description ?? "")
    }

    const handleCloseStageDialog = () => {
        setStageDialogState(null)
        setStageDialogTitle("")
        setStageDialogDescription("")
    }

    const handleConfirmDeleteStage = async () => {
        if (!stageToDelete) {
            return
        }

        const removed = await onRemoveStage(stageToDelete.id)

        if (removed) {
            setStageToDelete(null)
        }
    }

    const canMoveStage = (
        index: number,
        direction: "up" | "down"
    ) => {
        const targetIndex = direction === "up"
            ? index - 1
            : index + 1

        if (targetIndex < 0 || targetIndex >= sections.length) {
            return false
        }

        const sourceStageType = getStageUiType(sections[index])
        const targetStageType = getStageUiType(sections[targetIndex])

        if (sourceStageType === "EDIT" || targetStageType === "EDIT") {
            return false
        }

        return sourceStageType === targetStageType
    }

    return (
        <>
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ overflow: "hidden" }}
            >
                <Table
                    size="small"
                    sx={{ tableLayout: "fixed", width: "100%" }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell
                                colSpan={5}
                                sx={{
                                    pt: 0.5,
                                    pb: 1.75,
                                    px: 1,
                                    borderBottom: "none",
                                }}
                            >
                                <Box
                                    sx={theme => ({
                                        display: "flex",
                                        alignItems: {
                                            xs: "flex-start",
                                            md: "center",
                                        },
                                        justifyContent: "space-between",
                                        flexDirection: {
                                            xs: "column",
                                            md: "row",
                                        },
                                        gap: 1.5,
                                        border: "1px solid",
                                        borderColor: alpha(
                                            theme.palette.info.main,
                                            0.35
                                        ),
                                        bgcolor: alpha(
                                            theme.palette.info.main,
                                            0.04
                                        ),
                                        borderRadius: 1.5,
                                        px: 1.25,
                                        py: 1,
                                    })}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 700,
                                                color: "text.secondary",
                                            }}
                                        >
                                            Przebieg:
                                        </Typography>

                                        {sections.map((section, index) => {
                                            const stageType = getStageUiType(section)
                                            const isCurrent =
                                                section.isCurrent
                                                || section.id === currentStageId

                                            return (
                                                <Box
                                                    key={section.id}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Chip
                                                        label={`${index + 1}. ${stageTypeLabels[stageType]}`}
                                                        icon={stageTypeChipIcons[stageType]}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            ...getStageTypeChipSx(
                                                                stageType,
                                                                isCurrent
                                                            ),
                                                            borderRadius: 0.5,
                                                            height: 24,
                                                            px: 0.125,
                                                            "& .MuiChip-label": {
                                                                px: 0.75,
                                                                fontWeight: 600,
                                                                letterSpacing: 0.2,
                                                            },
                                                            "& .MuiChip-icon": {
                                                                ml: 0.625,
                                                                mr: -0.25,
                                                                fontSize: 15,
                                                            },
                                                        }}
                                                    />

                                                    {index < sections.length - 1 && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            →
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )
                                        })}
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 1,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => handleOpenAddStageDialog(
                                                "REVIEW"
                                            )}
                                        >
                                            Dodaj etap opiniowania
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => handleOpenAddStageDialog(
                                                "APPROVAL"
                                            )}
                                        >
                                            Dodaj etap zatwierdzania
                                        </Button>
                                    </Box>
                                </Box>
                            </TableCell>
                        </TableRow>

                        <TableRow
                            sx={{
                                "& > th": {
                                    borderTop: "1px solid",
                                    borderColor: "divider",
                                },
                            }}
                        >
                            <TableCell sx={{ width: 72, fontWeight: 600 }}>
                                Lp.
                            </TableCell>
                            <TableCell sx={{ minWidth: 280, fontWeight: 600 }}>
                                Nazwa etapu
                            </TableCell>
                            <TableCell sx={{ width: 150, fontWeight: 600 }}>
                                Typ
                            </TableCell>
                            <TableCell sx={{ width: 140, fontWeight: 600 }}>
                                Status
                            </TableCell>
                            <TableCell
                                sx={{
                                    width: 190,
                                    textAlign: "center",
                                    fontWeight: 600,
                                }}
                            >
                                Akcje
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {sections.map((section, index) => {
                            const stageType = getStageUiType(section)
                            const stageStatus = mapStageStatusToUi(section.status)
                            const isCurrent =
                                section.isCurrent
                                || section.id === currentStageId
                                || stageStatus.kind === "inProgress"
                            const isCompleted =
                                stageStatus.kind === "completed"

                            return (
                                <TableRow
                                    key={section.id}
                                    hover
                                    sx={
                                        isCurrent
                                            ? {
                                                bgcolor: "info.50",
                                                "& > td": {
                                                    borderColor: "info.light",
                                                },
                                            }
                                            : isCompleted
                                                ? {
                                                    bgcolor: "success.50",
                                                    "& > td": {
                                                        borderColor: "success.light",
                                                    },
                                                }
                                                : undefined
                                    }
                                >
                                    <TableCell>
                                        <Chip
                                            label={index + 1}
                                            size="small"
                                            color={
                                                isCurrent
                                                    ? "info"
                                                    : isCompleted
                                                        ? "success"
                                                        : "default"
                                            }
                                            sx={{
                                                minWidth: 32,
                                                fontWeight: 700,
                                            }}
                                        />
                                    </TableCell>

                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: isCurrent ? 700 : 500,
                                            }}
                                        >
                                            {section.name}
                                        </Typography>
                                        {section.description && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block", mt: 0.35 }}
                                            >
                                                {section.description}
                                            </Typography>
                                        )}
                                    </TableCell>

                                    <TableCell>
                                        <Chip
                                            label={stageTypeLabels[stageType]}
                                            icon={stageTypeChipIcons[stageType]}
                                            size="small"
                                            variant="outlined"
                                            sx={getStageTypeChipSx(
                                                stageType,
                                                isCurrent
                                            )}
                                        />
                                    </TableCell>

                                    <TableCell>
                                        <Chip
                                            label={stageStatus.label}
                                            size="small"
                                            color={stageStatus.color}
                                            variant={stageStatus.variant}
                                        />
                                    </TableCell>

                                    <TableCell sx={{ textAlign: "center" }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                gap: 0.25,
                                            }}
                                        >
                                            {showSetCurrentStageAction && (
                                                <Tooltip title="Ustaw jako biezacy etap">
                                                    <IconButton
                                                        size="small"
                                                        color={
                                                            isCurrent
                                                                ? "info"
                                                                : "default"
                                                        }
                                                        onClick={() => onSetCurrentStage(
                                                            section.id
                                                        )}
                                                    >
                                                        <PlayArrowIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            <Tooltip title="Edytuj etap">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenEditStageDialog(
                                                        section
                                                    )}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip
                                                title={
                                                    stageType === "EDIT"
                                                        ? "Etap opracowania jest wymagany"
                                                        : "Usun etap"
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={stageType === "EDIT"}
                                                        onClick={() => setStageToDelete(
                                                            section
                                                        )}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>

                                            <Tooltip title="Przesun w gore">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={!canMoveStage(
                                                            index,
                                                            "up"
                                                        )}
                                                        onClick={() => onMoveStage(
                                                            section.id,
                                                            "up"
                                                        )}
                                                    >
                                                        <KeyboardArrowUpIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>

                                            <Tooltip title="Przesun w dol">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={!canMoveStage(
                                                            index,
                                                            "down"
                                                        )}
                                                        onClick={() => onMoveStage(
                                                            section.id,
                                                            "down"
                                                        )}
                                                    >
                                                        <KeyboardArrowDownIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )
                        })}

                        {sections.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Alert severity="info" sx={{ my: 0.5 }}>
                                        Brak zdefiniowanych etapow workflow.
                                    </Alert>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <AddEditWorkflowStage
                open={stageDialogState !== null}
                mode={stageDialogState?.mode ?? "add"}
                documentId={documentId}
                stageType={stageDialogState?.stageType}
                stageId={stageDialogState?.stageId}
                stageApiType={stageDialogState?.stageApiType}
                title={stageDialogTitle}
                description={stageDialogDescription}
                onTitleChange={setStageDialogTitle}
                onDescriptionChange={setStageDialogDescription}
                onClose={handleCloseStageDialog}
                onSuccess={async () => {
                    await Promise.resolve(onStageSaved())
                    handleCloseStageDialog()
                }}
            />

            <DeleteWorkflowStage
                open={stageToDelete !== null}
                stageTitle={stageToDelete?.name}
                onClose={() => setStageToDelete(null)}
                onConfirm={handleConfirmDeleteStage}
            />
        </>
    )
}